<template>
    <div class="delivery-config-s3">
        <b-form-group label="Configuration Name" label-for="s3-name">
            <b-form-input id="s3-name" v-model="localConfig.name" placeholder="e.g., Analytics Reports Bucket"
                required />
        </b-form-group>

        <b-form-group label="Bucket Name" label-for="s3-bucket">
            <b-form-input id="s3-bucket" v-model="localConfig.bucketName" placeholder="my-bucket-name" required />
        </b-form-group>

        <b-form-group label="AWS Region" label-for="s3-region">
            <b-form-select id="s3-region" v-model="localConfig.region" :options="awsRegions" required />
        </b-form-group>

        <b-form-group label="Authentication Method">
            <b-form-radio-group v-model="authMethod" :options="authMethods" stacked />
        </b-form-group>

        <div v-if="authMethod === 'credentials'">
            <b-form-group label="Access Key ID" label-for="s3-access-key">
                <b-form-input id="s3-access-key" v-model="localConfig.accessKeyId" />
            </b-form-group>

            <b-form-group label="Secret Access Key" label-for="s3-secret-key">
                <b-form-input id="s3-secret-key" type="password" v-model="localConfig.secretAccessKey"
                    :placeholder="isEditing ? '(unchanged)' : 'Enter secret access key'" />
            </b-form-group>
        </div>

        <div v-if="authMethod === 'role'">
            <b-form-group label="Role ARN" label-for="s3-role-arn">
                <b-form-input id="s3-role-arn" v-model="localConfig.roleArn"
                    placeholder="arn:aws:iam::123456789012:role/RoleName" />
            </b-form-group>

            <b-form-group label="External ID (optional)" label-for="s3-external-id">
                <b-form-input id="s3-external-id" v-model="localConfig.externalId"
                    placeholder="External ID for cross-account access" />
            </b-form-group>
        </div>

        <div class="d-flex justify-content-end mt-3">
            <b-button variant="outline-primary" size="sm" @click="testConnection" :disabled="testing">
                <b-spinner v-if="testing" small class="mr-1" />
                {{ testing ? 'Testing...' : 'Test Connection' }}
            </b-button>
        </div>

        <b-alert v-if="testResult" :variant="testResult.success ? 'success' : 'danger'" show class="mt-2 mb-0">
            {{ testResult.message }}
        </b-alert>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Watch, Mixins } from "vue-property-decorator";
import { S3ConfigRequest, reportExportService } from "@/services/reportExportService";
import ConnectionTestMixin from "@/mixins/ConnectionTestMixin";

@Component
export default class DeliveryConfigS3 extends Mixins(ConnectionTestMixin) {
    @Prop({ default: () => ({}) }) value!: S3ConfigRequest;
    @Prop({ default: false }) isEditing!: boolean;

    private localConfig: S3ConfigRequest = {
        name: "",
        bucketName: "",
        region: "us-east-1",
        accessKeyId: "",
        secretAccessKey: "",
        roleArn: "",
        externalId: ""
    };

    private authMethod = "default";
    private isInitializing = false;

    private authMethods = [
        { value: "default", text: "Use Default Credentials (IAM Role)" },
        { value: "credentials", text: "Explicit Access Key/Secret" },
        { value: "role", text: "Assume IAM Role (Cross-Account)" }
    ];

    private awsRegions = [
        { value: "us-east-1", text: "US East (N. Virginia)" },
        { value: "us-east-2", text: "US East (Ohio)" },
        { value: "us-west-1", text: "US West (N. California)" },
        { value: "us-west-2", text: "US West (Oregon)" },
        { value: "eu-west-1", text: "Europe (Ireland)" },
        { value: "eu-west-2", text: "Europe (London)" },
        { value: "eu-central-1", text: "Europe (Frankfurt)" },
        { value: "ap-southeast-1", text: "Asia Pacific (Singapore)" },
        { value: "ap-southeast-2", text: "Asia Pacific (Sydney)" },
        { value: "ap-northeast-1", text: "Asia Pacific (Tokyo)" }
    ];

    mounted() {
        this.initializeFromValue();
    }

    @Watch("value", { deep: true })
    onValueChange() {
        if (!this.isInitializing) {
            this.initializeFromValue();
        }
    }

    @Watch("localConfig", { deep: true })
    onLocalConfigChange() {
        if (!this.isInitializing) {
            this.$emit("input", { ...this.localConfig });
        }
    }

    private initializeFromValue() {
        if (this.value) {
            this.isInitializing = true;
            this.localConfig = {
                id: this.value.id,
                name: this.value.name || "",
                bucketName: this.value.bucketName || "",
                region: this.value.region || "us-east-1",
                accessKeyId: this.value.accessKeyId || "",
                secretAccessKey: this.value.secretAccessKey || "",
                roleArn: this.value.roleArn || "",
                externalId: this.value.externalId || ""
            };

            // Determine auth method from existing config
            if (this.localConfig.roleArn) {
                this.authMethod = "role";
            } else if (this.localConfig.accessKeyId) {
                this.authMethod = "credentials";
            } else {
                this.authMethod = "default";
            }

            this.$nextTick(() => {
                this.isInitializing = false;
            });
        }
    }

    private async testConnection() {
        await this.performConnectionTest(() =>
            reportExportService.testS3Connection(this.localConfig));
    }
}
</script>

<style scoped>
.delivery-config-s3 {
    padding: 1rem;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    background-color: #f8f9fa;
}
</style>
